import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ContextService } from '@bcef/ia-shared';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, Observable, of, switchMap } from 'rxjs';

export interface Agency {
  agencyCode: string;
  streetNumberAndName: string | null;
  postalCode: string | null;
  city: string | null;
  collaboratorPhone: string | null;
  collaboratorMobilePhone: string | null;
  crcAddress: string | null;
}

export interface AgencyServiceResponse {
  items: [Agency];
}

export interface ComplaintsDepartmentResponse {
  departments: {
    id: string;
    lib: string;
    adr1: string;
    adr2: string;
    cp: string;
    ville: string;
  }[];
  agencies: { code: string; id: string }[];
}

@Injectable({
  providedIn: 'root',
})
export class AgencyService {
  private readonly http = inject(HttpClient);

  private readonly translate = inject(TranslateService);

  private readonly isBpf = inject(ContextService).isBpf;

  public getAgency(): Observable<Agency | null> {
    return this.http.get<AgencyServiceResponse>(`/rgg/advisor-api-manager/v1/advisors`).pipe(
      switchMap(({ items }) => {
        const data = items[0] ?? null;
        if (data.agencyCode && !this.isBpf) {
          return this.getComplaintDepartmentAddress(data.agencyCode).pipe(
            map((crcAddress) => ({ ...data, crcAddress })),
          );
        }
        return of(data);
      }),
      catchError(() => of(null)),
    );
  }

  public getComplaintDepartmentAddress(agencyId: string): Observable<string> {
    return this.http
      .get<ComplaintsDepartmentResponse>(`/apps/ia-make-complaint/complaint-department.json`)
      .pipe(
        map(({ departments, agencies }) => {
          const agency = agencies.find(({ code }) => code === agencyId);
          if (!agency?.id) {
            return '';
          }

          const department = departments.find(({ id }) => id === agency?.id);
          if (department) {
            return [
              this.translate.instant('screens.notSatisfiedPanel.dep'),
              department.adr1,
              department.adr2,
              department.cp,
              department.ville,
            ]
              .join(', ')
              .trim();
          } else {
            return '';
          }
        }),
        catchError(() => of('')),
      );
  }
}
